<template>
  <div class="card" style="min-height: 450px">
    <div class="card-body">
      <div class="row mb-4">
        <div class="col-md-6">
          <div class="d-flex flex-wrap mc-dashboard__fuel">
            <div>
              <h5 class="mb-1">
                {{ $t('dashboard.title.fuelUsageTotal') }}
              </h5>
              <p class="mb-0 font-size-10">
                {{ momentDateShortMonth(periode?.[0]) }} -
                {{ momentDateShortMonth(periode?.[1]) }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="float-end">
            <div class="mc-table-filter">
              <div class="mc-table-view z-index-0">
                <p class="mc-label">{{ $t('global.object.period') }}</p>
                <mc-date-picker
                  class="ms-2"
                  v-model="periode"
                  :placeholder="$t('global.action.selectDate')"
                  format="DD MMM YYYY"
                  value-type="YYYY-MM-DD"
                  :shortcuts="makeShortcuts"
                  :draft="true"
                  :disabled="isLayoutEdit"
                  range-separator=" - "
                  range
                  :confirm="true"
                  :confirm-text="$t('global.action.apply')"
                  ref="periodePicker"
                  :disabled-date="disabledRange"
                  @pick="pickDate"
                  @input="generateSumFuel(vgroupId)"
                  :clearable="false"
                  :lang="datePickerLang"
                ></mc-date-picker>
                <a class="ms-2" v-if="phaseOneShow">
                  <i class="mc-fa icon-outline_more-vertical"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <div class="row">
            <div class="col-md-8">
              <b-card>
                <b-skeleton width="85%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="70%"></b-skeleton>
              </b-card>
              <b-card>
                <b-skeleton width="85%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="70%"></b-skeleton>
              </b-card>
            </div>
            <div class="col-md-4">
              <b-card>
                <b-skeleton width="85%"></b-skeleton>
                <b-skeleton width="55%"></b-skeleton>
                <b-skeleton width="70%"></b-skeleton>
              </b-card>
            </div>
          </div>
        </template>
        <div class="row">
          <div class="col-md-3">
            <div class="mc-dashboard-fuel-moving__total">
              <i class="mc-fa icon-outline_pin"></i>
              <div class="d-flex flex-wrap">
                <div>
                  <p class="teal-400 mb-1 text-white" style="font-size: 16px">
                    {{ $t('dashboard.text.summaryTrip') }}
                  </p>
                </div>
              </div>
              <div class="mc-dashboard-fuel-moving__total-wrapper">
                <div class="d-flex flex-wrap justify-content-between align-items-center">
                  <span style="font-size: 14px">{{ $t('dashboard.text.tripDistance') }}</span>
                  <div class="d-flex flex-wrap align-items-end">
                    <span class="text-white fw-bold" style="font-size: 32px"
                      >{{ sumTotalDistance }} <span style="font-size: 10px">Km</span>
                    </span>
                  </div>
                </div>
                <div class="mt-4 d-flex flex-wrap justify-content-between align-items-center">
                  <span style="font-size: 14px">{{ $t('dashboard.text.tripDuration') }}</span>
                  <div>
                    <span class="text-white fw-bold" style="font-size: 32px">{{
                      sumTotalHours
                    }}</span>
                    <br />
                    <span class="text-white" style="font-size: 10px"
                      >({{ $t('global.time.hour') }})</span
                    >
                    <span class="ps-1 text-white" style="font-size: 10px"
                      >({{ $t('global.time.minute') }})</span
                    >
                    <span class="ps-1 text-white" style="font-size: 10px"
                      >({{ $t('global.time.second') }})</span
                    >
                  </div>
                </div>
              </div>
              <br />
            </div>
          </div>
          <div class="col-md-6">
            <b-card class="standard-fuel">
              <h5>{{ $t('dashboard.text.standard') }}</h5>
              <div class="row">
                <div class="col-md-6">
                  <h1 class="standard-label">
                    {{ standardFuel }} <span>{{ $t('dashboard.text.litre') }}</span>
                  </h1>
                </div>
                <div class="col-md-6">
                  <h1 class="standard-label">{{ standardPrice }}</h1>
                </div>
              </div>
            </b-card>
            <b-card class="actual-fuel">
              <h5>{{ $t('dashboard.text.actual') }}</h5>
              <div class="row">
                <div class="col-md-6">
                  <h1 class="actual-label">
                    {{ actualFuel }} <span>{{ $t('dashboard.text.litre') }}</span>
                  </h1>
                </div>
                <div class="col-md-6">
                  <h1 class="actual-label">{{ actualPrice }}</h1>
                </div>
              </div>
            </b-card>
          </div>
          <div class="col-md-3">
            <div class="card fuel-difference">
              <div class="card-header header">
                <h3 class="header-label">{{ $t('dashboard.text.difference') }}</h3>
                <h5 class="header-label">
                  {{ $t('dashboard.text.standard') }} - {{ $t('dashboard.text.actual') }}
                </h5>
              </div>
              <div class="card-body">
                <h1 class="body-label">
                  {{ minimizeFloat(fuelDifference) }} <span>{{ $t('dashboard.text.litre') }}</span>
                </h1>
                <h3>
                  <span
                    class="badge rounded-pill mx-2"
                    :class="fuelDifference < 0 ? 'bg-danger' : 'bg-success'"
                    >{{
                      fuelDifference < 0
                        ? $t('dashboard.text.wasteful')
                        : $t('dashboard.text.normal')
                    }}</span
                  >
                </h3>
                <div class="horizontal-line"></div>
                <h4 class="body-label">{{ $t('dashboard.text.potential') }}</h4>
                <h1 class="body-label">{{ priceDifference }}</h1>
              </div>
            </div>
          </div>
        </div>
      </b-skeleton-wrapper>
    </div>
  </div>
</template>
<script>
import McDatePicker from '@/components/McDatePicker'
import { Helper } from '@/mixin/helper'
import { GET_REPORT_SUMMARY_FUEL } from '@/services/api/report/report'
import moment from 'moment'
import datepickerShortcuts from '../lib/constants/date-picker-shortcuts'
import datePickerLang from '@/helpers/date-picker-lang'
import { mapGetters } from 'vuex'

moment.locale('id')

export default {
  components: {
    McDatePicker,
  },
  mixins: [Helper],
  props: {
    isLayoutEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      phaseOneShow: false,
      isLoading: false,
      column: [
        'license_plate',
        'vehicle_type',
        'count_refueling',
        'count_fuel_loss',
        'total_fuel',
        'total_distance',
        'total_hours',
        'idle_duration',
        'actual_efficiency',
        'standard_efficiency',
        'price',
        'standard_fuel',
        'standard_price',
        'actual_total_fuel',
        'actual_price',
      ],
      periode: [moment().subtract(6, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      standardFuel: null,
      standardPrice: null,
      actualFuel: null,
      actualPrice: null,
      fuelDifference: null,
      priceDifference: null,
      sumTotalDistance: null,
      sumTotalHours: null,
      pastDate: moment().subtract(1, 'years'),
      futureEnd: moment().add(1, 'years'),
      vgroupId: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    makeShortcuts() {
      return datepickerShortcuts(this, 'fuel-usage-config', { column: this.column })
    },
    datePickerLang() {
      return datePickerLang()
    },
  },
  async mounted() {
    this.isLoading = true
    let config = JSON.parse(localStorage.getItem('fuel-usage-config'))
    let period = []
    if (!config) {
      localStorage.setItem(
        'fuel-usage-config',
        JSON.stringify({
          startDate: moment(this.periode[0] + ' 00:00:00.000').toISOString(),
          endDate: moment(this.periode[1] + ' 23:59:59.000').toISOString(),
          column: this.column,
          isShortcut: false,
          index: 0,
        })
      )
      config = JSON.parse(localStorage.getItem('fuel-usage-config'))
    }
    if (config.isShortcut) {
      const generateDate = this.makeShortcuts[config.index].onClick()
      period[0] = this.momentYmD(generateDate[0])
      period[1] = this.momentYmD(generateDate[1])
    } else {
      period[0] = config.startDate == null ? this.periode[0] : this.momentYmD(config.startDate)
      period[1] = config.endDate == null ? this.periode[1] : this.momentYmD(config.endDate)
    }
    this.periode = period
    await this.generateSumFuel(this.vgroupId)
  },
  methods: {
    disabledRange: function (date) {
      return date <= this.pastDate || date >= this.futureEnd || date > new Date()
    },
    pickDate(date) {
      let maxDate = 30
      this.pastDate = new Date(date.getTime() - 24 * maxDate * 3600 * 1000)
      // Get the current date and time
      let currentDate = new Date()

      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000).getTime() === currentDate.getTime()) {
        this.futureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.futureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },
    initData(item) {
      this.isLoading = true
      this.standardFuel = item.summary.sumStandardFuel
      let standardPrice = parseFloat(item.summary.sumStandardPrice)
      this.standardPrice = new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
      }).format(standardPrice)
      this.actualFuel = item.summary.sumActualTotalFuel
      let actualPrice = parseFloat(item.summary.sumActualPrice)
      this.actualPrice = new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
      }).format(actualPrice)
      this.fuelDifference = parseFloat(this.standardFuel) - parseFloat(this.actualFuel)
      let priceDiff = standardPrice - actualPrice
      this.priceDifference = new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
      }).format(priceDiff)
      this.sumTotalDistance = item.summary.sumTotalDistance
      this.sumTotalHours = item.summary.sumTotalHours
      setTimeout(() => {
        this.isLoading = false
      }, 200)
    },
    async generateSumFuel(vgroupId) {
      this.vgroupId = vgroupId
      const config = JSON.parse(localStorage.getItem('fuel-usage-config'))
      const filter = {
        startDate: moment(this.periode[0] + ' 00:00:00.000').toISOString(),
        endDate: moment(this.periode[1] + ' 23:59:59.000').toISOString(),
        column: this.column,
        isShortcut: false,
        index: 0,
      }
      if (!config.isShortcut) {
        localStorage.setItem('fuel-usage-config', JSON.stringify(filter))
      }
      const res = await this.$apollo.query({
        query: GET_REPORT_SUMMARY_FUEL,
        variables: {
          filter: {
            startDate: filter.startDate,
            endDate: filter.endDate,
            column: filter.column,
            vgroupId,
            disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
          },
        },
        fetchPolicy: 'no-cache',
      })
      this.initData(res.data.summaryFuel.summaryFuel)
    },
  },
}
</script>
<style lang="scss">
.z-index-0 {
  z-index: 0 !important;
}
.standard-fuel {
  min-height: 147.5px;
  background: linear-gradient(0deg, #fff3e6, #fff3e6), linear-gradient(0deg, #ffd099, #ffd099);
  border: 1px solid #ffd099;
  border-radius: 8px;
  .row {
    height: 80px;
  }
  .col-md-6 {
    flex: 1;
    text-align: center;
  }
  .col-md-6:not(:last-child) {
    border-right: 4px solid #ff8a00; /* Vertical line between columns */
    padding-right: 10px; /* Adjust padding to control spacing */
    margin-right: 10px; /* Adjust margin to control spacing */
    height: 100%;
  }
  .standard-label {
    text-align: center !important;
    margin-top: 20px;
    color: #ff8a00;
    span {
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      letter-spacing: 0px;
      text-align: center;
    }
  }
}
.actual-fuel {
  min-height: 147.5px;
  background: linear-gradient(0deg, #deeafc, #deeafc), linear-gradient(0deg, #9dc1f0, #9dc1f0);
  border: 1px solid #9dc1f0;
  border-radius: 8px;
  .row {
    height: 80px;
  }
  .col-md-6 {
    flex: 1;
    text-align: center;
  }
  .col-md-6:not(:last-child) {
    border-right: 4px solid #1c74da; /* Vertical line between columns */
    padding-right: 10px; /* Adjust padding to control spacing */
    margin-right: 10px; /* Adjust margin to control spacing */
    height: 100%;
  }
  .actual-label {
    text-align: center !important;
    margin-top: 20px;
    color: #1c74da;
    span {
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      letter-spacing: 0px;
      text-align: center;
    }
  }
}
.fuel-difference {
  min-height: 311px;
  background: linear-gradient(149.71deg, #0d4491 -1.83%, #1c74da 105.06%),
    linear-gradient(0deg, #0d4491, #0d4491);
  border-radius: 8px;
  border: 1px solid #0d4491;
  text-align: center !important;
  .header {
    background: #deeafc;
  }
  .header-label {
    color: #1c74da;
  }
  .body-label {
    color: #ffffff;
    span {
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      letter-spacing: 0px;
      text-align: center;
    }
  }
  .horizontal-line {
    border-top: 2px solid #ffffff; /* Customize line color and style as needed */
    margin: 20px 0; /* Adjust margin to control spacing */
  }
}
</style>
